import React from 'react';

import { faEye, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { Col, Container, Row, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

import TablePagination from '../TablePagination';
import './styles.scoped.css';

const utc = require('dayjs/plugin/utc');

dayjs.extend(utc);

function getSortIcon(isSorted, isSortedDesc) {
  let iconProp = faSort;
  if (isSorted) {
    iconProp = isSortedDesc ? faSortDown : faSortUp;
  }
  return <FontAwesomeIcon icon={iconProp} className="ml-1 fa-sm" />;
}

const columnClassName = 'align-middle text-nowrap';

const columns = [
  {
    Header: 'Created Date',
    accessor: 'createdAt',
    className: columnClassName,
    Cell: instance => <>{`${dayjs.utc(instance?.row?.original?.createdAt).local().format('MM/DD/YYYY')}`}</>,
  },
  {
    Header: 'Created By',
    accessor: 'createdBy',
    className: columnClassName,
  },
  {
    Header: 'Subject',
    accessor: 'subject',
    className: columnClassName,
  },
  {
    Header: 'Type',
    accessor: 'type',
    className: columnClassName,
  },
  {
    Header: 'Status',
    accessor: 'status',
    className: columnClassName,
  },
  {
    Header: 'Approved By',
    accessor: 'approvedBy',
    className: columnClassName,
  },
  {
    Header: 'Sent On',
    accessor: 'sentOn',
    className: columnClassName,
    Cell: instance => (
      <>
        {instance?.value !== null ? `${dayjs.utc(instance?.row?.original?.sentOn).local().format('MM/DD/YYYY')}` : ''}
      </>
    ),
  },
  {
    accessor: 'Actions',
    className: columnClassName,
    Cell: instance => (
      <Link to={`/push-notifications/centerline-survey/view-centerline-survey/${instance?.row?.original?.id}`}>
        <FontAwesomeIcon icon={faEye} className="icon-color fa-md" />
      </Link>
    ),
  },
];

export default function NationalPushNotificationsTable({ data }) {
  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [
          {
            id: 'createdAt',
            desc: true,
          },
        ],
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } = tableInstance;
  const tableHeaders = headerGroups.map(headerGroup => {
    const headers = headerGroup.headers.map(column => (
      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
        {column.render('Header')}
        <span>{column.canSort ? getSortIcon(column.isSorted, column.isSortedDesc) : null}</span>
      </th>
    ));
    return <tr {...headerGroup.getHeaderGroupProps()}>{headers}</tr>;
  });

  const tableRows = page.map(row => {
    prepareRow(row);
    const cells = row.cells.map(cell => (
      <td
        {...cell.getCellProps({
          className: cell.column.className,
          style: cell.column.style,
        })}
      >
        {cell.render('Cell')}
      </td>
    ));

    return <tr {...row.getRowProps()}>{cells}</tr>;
  });

  return (
    <Container fluid>
      <Row className="header">
        <Col className="d-flex justify-content-end">
          <TablePagination tableInstance={tableInstance} />
        </Col>
      </Row>
      <Row className="table-row">
        <Col className="table-body">
          <Table hover responsive borderless {...getTableProps()}>
            <thead className="text-nowrap">{tableHeaders}</thead>
            <tbody {...getTableBodyProps()}>{tableRows}</tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
}
